/**
 * DOCUMENTATION:
 * Component: GCPGKE
 * Purpose: Deep dive into Google Kubernetes Engine (GKE) for the PCA Exam.
 * * * Note for Junior Developers:
 * - Kubernetes (K8s): An orchestration system for containers.
 * - GKE: Google's managed version where they handle the "Control Plane" for you.
 * - Pod: The smallest unit. It holds your container (Docker).
 * - Autopilot: A "hands-off" mode where Google manages nodes, scaling, and security.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Divider, List, ListItem, ListItemText, ListItemIcon, Paper
} from "@mui/material";

/* Icons */
import HubIcon from '@mui/icons-material/Hub';
import LayersIcon from '@mui/icons-material/Layers';
import SecurityIcon from '@mui/icons-material/Security';
import TsunamiIcon from '@mui/icons-material/Tsunami'; // Representing scaling
import DnsIcon from '@mui/icons-material/Dns';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d", // GCP Theme Blue
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

const ModeBox = styled(Box)(({ theme, color }) => ({
    padding: theme.spacing(3),
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    border: `1px solid \${color}`,
    height: "100%",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.05)",
    }
}));

export default function GCPGKE() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    KUBERNETES ENGINE (GKE)
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Orchestration at Google Scale</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. The Anatomy of GKE */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <LayersIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Cluster Architecture</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.8 }}>
                            A GKE cluster consists of a **Control Plane** (managed by Google) and **Worker Nodes** (Compute Engine VMs).
                            The Control Plane decides which pods run on which nodes and handles the API server.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" color="#4285F4" fontWeight={700}>Control Plane</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Includes the API Server, Scheduler, and etcd. In GKE, Google manages backups and upgrades of these components for you.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" color="#34A853" fontWeight={700}>Nodes (Data Plane)</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    The VMs that run your containers. They are grouped into **Node Pools** to allow different machine types for different workloads.
                                </Typography>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Autopilot vs Standard */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <VerifiedUserIcon sx={{ color: "#FBBC04", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Operation Modes</Typography>
                        </Stack>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <ModeBox color="#FBBC04">
                                    <Typography variant="h6" fontWeight={800} gutterBottom>GKE Autopilot</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                        **The "Hands-Off" Choice.** Google manages the entire infrastructure, including node provisioning, scaling, and security hardening.
                                        You pay per Pod resource request.
                                    </Typography>
                                    <Typography variant="caption" color="#FBBC04">✓ Best for most modern apps and lean teams.</Typography>
                                </ModeBox>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ModeBox color="#4285F4">
                                    <Typography variant="h6" fontWeight={800} gutterBottom>GKE Standard</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                                        **The "Maximum Control" Choice.** You manage the node configurations. You have full access to the nodes and can use specialized hardware. You pay per VM.
                                    </Typography>
                                    <Typography variant="caption" color="#4285F4">✓ Best for custom kernels, specific hardware, or extreme cost tuning.</Typography>
                                </ModeBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. Availability and Scaling */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <HubIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>High Availability (HA)</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                            In the PCA exam, you must choose between **Zonal** and **Regional** clusters.
                        </Typography>



                        <Stack spacing={2}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                <Typography variant="subtitle2" color="#34A853">Regional Clusters (Recommended)</Typography>
                                <Typography variant="caption">The Control Plane and Nodes are spread across 3 zones. If a whole datacenter (zone) fails, your cluster survives.</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                <Typography variant="subtitle2" color="#EA4335">Zonal Clusters</Typography>
                                <Typography variant="caption">The Control Plane is in one zone. If that zone fails, you lose management access to your cluster.</Typography>
                            </Paper>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 4. GKE Security & Networking */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SecurityIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Security Pillars</Typography>
                        </Stack>
                        <List>
                            <ListItem>
                                <ListItemIcon><DnsIcon sx={{ color: "#4285F4" }} /></ListItemIcon>
                                <ListItemText
                                    primary="VPC-Native Clusters"
                                    secondary="Uses Alias IPs. It's faster and allows direct communication with resources outside the cluster."
                                    secondaryTypographyProps={{ style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><VerifiedUserIcon sx={{ color: "#34A853" }} /></ListItemIcon>
                                <ListItemText
                                    primary="Binary Authorization"
                                    secondary="Ensures only trusted, signed images are deployed to your cluster."
                                    secondaryTypographyProps={{ style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><TsunamiIcon sx={{ color: "#FBBC04" }} /></ListItemIcon>
                                <ListItemText
                                    primary="Workload Identity"
                                    secondary="The most secure way for Pods to call GCP APIs without using service account keys."
                                    secondaryTypographyProps={{ style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } }}
                                />
                            </ListItem>
                        </List>
                    </SectionCard>
                </Grid>

                {/* Exam Scenario */}
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ bgcolor: "rgba(66, 133, 244, 0.1)", color: "white", border: "1px solid #4285F4" }}>
                        <strong>PCA ARCHITECT SCENARIO:</strong> "Your application requires 99.99% availability and needs to scale automatically based on memory usage."
                        <br/><br/>
                        <strong>Correct Strategy:</strong> Deploy a **Regional GKE Cluster** using **Horizontal Pod Autoscaling (HPA)** for the app, and **Cluster Autoscaler** to add more nodes to the pool when needed.
                    </Alert>
                </Grid>

                {/* Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
